import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import authRecoveryService from '../../../services/authRecoveryService';

const SupabaseConfigPanel = ({ refreshTrigger, onValidationComplete }) => {
  const [validationHistory, setValidationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [latestValidation, setLatestValidation] = useState(null);

  useEffect(() => {
    loadValidationHistory();
  }, [refreshTrigger]);

  const loadValidationHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authRecoveryService?.getConfigurationValidationHistory(10);
      setValidationHistory(data);
      if (data?.length > 0) {
        // Group latest validations by type
        const latest = {};
        data?.forEach(validation => {
          if (!latest?.[validation?.validationType]) {
            latest[validation?.validationType] = validation;
          }
        });
        setLatestValidation(latest);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load validation history');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      setError('');
      const result = await authRecoveryService?.validateSupabaseConfiguration();
      alert(`Validation complete: ${result?.overallStatus}`);
      await loadValidationHistory();
      onValidationComplete?.();
    } catch (err) {
      setError(err?.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'warning':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'failed':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  if (loading && validationHistory?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Supabase Configuration Validator
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Real-time connection testing, API key verification, and database schema validation
          </p>
        </div>
        <Button
          onClick={handleValidate}
          disabled={validating}
          loading={validating}
        >
          <Icon name="Play" size={14} className="mr-1" />
          Run Validation
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Latest Validation Results */}
      {latestValidation && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Latest Validation Results</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object?.entries(latestValidation)?.map(([type, validation]) => (
              <div
                key={type}
                className="bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getStatusIcon(validation?.validationStatus)} 
                      size={16} 
                      className={validation?.validationStatus === 'passed' ? 'text-green-600 dark:text-green-400' : validation?.validationStatus === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}
                    />
                    <h5 className="text-xs font-semibold text-gray-900 dark:text-white">
                      {type?.replace('_', ' ')?.toUpperCase()}
                    </h5>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded ${getStatusColor(validation?.validationStatus)}`}>
                    {validation?.validationStatus?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {new Date(validation?.validatedAt)?.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation History */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Validation History</h4>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Validated At</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {validationHistory?.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
                      <Icon name="Database" className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No validation history available</p>
                      <p className="text-xs mt-1">Run your first validation to see results</p>
                    </td>
                  </tr>
                ) : (
                  validationHistory?.map((validation) => (
                    <tr key={validation?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {validation?.validationType?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusColor(validation?.validationStatus)}`}>
                          {validation?.validationStatus?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {validation?.errorMessage || JSON.stringify(validation?.validationDetails)?.substring(0, 50) || 'OK'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {new Date(validation?.validatedAt)?.toLocaleString()}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {validation?.validatedBy || 'System'}
                        </p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Diagnostic Information */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Icon name="Info" className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">What Gets Validated</p>
            <ul className="text-xs text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
              <li><strong>Connection:</strong> Database connectivity and response time</li>
              <li><strong>Database Schema:</strong> Critical tables existence and structure</li>
              <li><strong>RLS Policies:</strong> Row-level security configuration</li>
              <li><strong>API Keys:</strong> Anon key validity and permissions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupabaseConfigPanel;